import { useEffect, useRef, useState } from "react";
import { Icon } from "../../kit";
import { useViewer } from "../../../state/ViewerProvider";
import { notice, useUi } from "../../../state/editor/useEditor";
import { LOOK_CATEGORIES, lookField, type LookCategory } from "../../../shared/lookFields";
import { getLookMatchStatus, requestLookMatch, type LookMatchResponse } from "../../../features/ai/lookMatchApi";
import { RAIL_W, RIGHT_PANEL_W } from "../layout";
import { ScrollArea } from "../ScrollArea";
import { AI_PANEL } from "./mode";
import { prepareImage, type PreparedImage } from "./imagePrep";
import { resolveRow } from "./targets";
import { useBasicEngine } from "./useBasicEngine";
import type { LookValue } from "./applyEngine";

/**
 * AI Look Match: drop a photo of the look you want; the AI proposes values for
 * the same look fields Basic mode drives, and they are applied through the same
 * engine — one Undo step, visible on the Advanced sliders.
 *
 * The CURRENT RENDER goes along with the reference, so the model compares two
 * pictures instead of guessing from numbers; "Refine" sends a fresh render of
 * the result, which is how a second pass closes the remaining gap.
 */

type Status = "checking" | "ready" | "signedOut" | "unavailable";
type Phase = "idle" | "working" | "done" | "error";

const STAGES = ["Looking at your photo…", "Comparing it with your render…", "Choosing settings…", "Almost there…"];

function fmt(v: LookValue | undefined): string {
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(Math.abs(v) < 0.1 ? 3 : 2);
  if (typeof v === "boolean") return v ? "on" : "off";
  return v ?? "—";
}

/**
 * `embedded`: render just the body, for Basic mode's left panel (AI Match is
 * on Basic's rail, and Scenes links to it as "Match a photo"). Without it, the
 * Advanced right-rail shell as before.
 */
export function AiMatchPanel({ embedded = false }: { embedded?: boolean } = {}) {
  const [ui, patchUi] = useUi();
  const engine = useBasicEngine();
  const { client } = useViewer();
  const [status, setStatus] = useState<Status>("checking");
  const [refs, setRefs] = useState<PreparedImage[]>([]);
  const [cats, setCats] = useState<LookCategory[]>(LOOK_CATEGORIES.map((c) => c.id));
  const [note, setNote] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [stage, setStage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ res: LookMatchResponse; before: Record<string, LookValue> } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let alive = true;
    let signedIn = false;
    try { signedIn = !!localStorage.getItem("auth_token"); } catch { /* private mode */ }
    if (!signedIn) { setStatus("signedOut"); return; }
    getLookMatchStatus()
      .then((s) => alive && setStatus(s.lookMatch ? "ready" : "unavailable"))
      .catch(() => alive && setStatus("unavailable"));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (phase !== "working") return;
    setStage(0);
    const t = window.setInterval(() => setStage((s) => Math.min(STAGES.length - 1, s + 1)), 7000);
    return () => window.clearInterval(t);
  }, [phase]);

  // Thumbnail object URLs: released when an image is removed (removeRef) and,
  // for whatever is still listed, when the panel unmounts — never on a plain
  // re-render, which would blank thumbnails that are still on screen.
  const refsNow = useRef<PreparedImage[]>([]);
  refsNow.current = refs;
  useEffect(() => () => refsNow.current.forEach((r) => URL.revokeObjectURL(r.previewUrl)), []);

  if (!embedded && ui.rightTab !== AI_PANEL) return null;

  const addFiles = async (files: File[]) => {
    const imgs = files.filter((f) => f.type.startsWith("image/"));
    if (imgs.length === 0) { setError("Drop a JPEG, PNG or WebP photo."); return; }
    const room = 3 - refs.length;
    if (room <= 0) { setError("Up to three reference photos."); return; }
    try {
      const prepared = await Promise.all(imgs.slice(0, room).map((f) => prepareImage(f)));
      setRefs((r) => [...r, ...prepared]);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const removeRef = (i: number) => {
    const gone = refs[i];
    if (gone) URL.revokeObjectURL(gone.previewUrl);
    setRefs((r) => r.filter((_, j) => j !== i));
  };

  /** Our render right now, at the canvas aspect, as the model's "before". */
  const captureCurrent = async (): Promise<PreparedImage | null> => {
    try {
      const frame = document.querySelector("main iframe")?.getBoundingClientRect();
      const aspect = frame && frame.width > 0 && frame.height > 0 ? frame.height / frame.width : 1;
      const w = 1280;
      const h = Math.max(320, Math.min(1568, Math.round(w * aspect)));
      const blob = await client.ring.snapshot({ width: w, height: h, format: "jpeg", quality: 0.88, frames: 32 });
      return await prepareImage(blob);
    } catch {
      return null; // the model can still work from the numbers alone
    }
  };

  const run = async () => {
    if (refs.length === 0 || cats.length === 0) return;
    setPhase("working");
    setError(null);
    try {
      await engine.ensureMaterials();
      const before = engine.currentValues(cats);
      const current = await captureCurrent();
      const res = await requestLookMatch({
        references: refs.map(({ mediaType, data }) => ({ mediaType, data })),
        ...(current ? { current: { mediaType: current.mediaType, data: current.data } } : {}),
        categories: cats,
        ...(note.trim() ? { note: note.trim() } : {}),
        values: before,
      });
      if (current) URL.revokeObjectURL(current.previewUrl);
      if (res.changes.length > 0) {
        await engine.setValues(res.changes.map((c) => ({ key: c.key, value: c.value })), "AI Look Match", undefined, "aiMatch");
      }
      setResult({ res, before });
      setPhase("done");
    } catch (e) {
      setError((e as Error).message || "Look Match failed.");
      setPhase("error");
    }
  };

  const seeInAdvanced = () => {
    const rows = engine.lastStep?.controlId === "aiMatch" ? engine.lastStep.rows : [];
    const first = rows.map(resolveRow).find(Boolean);
    if (!first) return;
    const collapsed = { ...ui.collapsed };
    for (const r of rows) {
      const hit = resolveRow(r);
      if (hit && hit.tab === first.tab) collapsed[`${hit.tab}.${hit.section}`] = false;
    }
    patchUi({
      inspectorMode: "advanced",
      rightTab: first.tab,
      lastRightTab: first.tab,
      lastAdvancedTab: first.tab,
      collapsed,
      highlight: { rows, at: Date.now() },
    });
  };

  const working = phase === "working";
  const canRun = status === "ready" && refs.length > 0 && cats.length > 0 && !working;

  const onPaste = (e: React.ClipboardEvent) => {
    const files = [...e.clipboardData.files];
    if (files.length) { e.preventDefault(); void addFiles(files); }
  };

  const content = (
        <>
          <p className="text-[11px] leading-snug text-ed-text-3">
            Show the AI a photo of the look you want — a diamond close-up, a finished ring shot, a shadow style. It sets
            the studio to match and you can see every slider it moved.
          </p>

          {status === "signedOut" ? (
            <Callout icon="lock">Sign in to use AI Look Match.</Callout>
          ) : status === "unavailable" ? (
            <Callout icon="cloud_off">AI Look Match isn’t available on this server yet.</Callout>
          ) : null}

          {/* Reference photos */}
          <div>
            <div className="mb-1.5 text-[12px] font-semibold text-ed-text">Reference photos</div>
            <div className="grid grid-cols-3 gap-1.5">
              {refs.map((r, i) => (
                <div key={r.previewUrl} className="group relative aspect-square overflow-hidden rounded-md border border-ed-border bg-ed-panel-2">
                  <img src={r.previewUrl} alt={`Reference ${i + 1}`} className="h-full w-full object-cover" />
                  <button
                    title="Remove"
                    onClick={() => removeRef(i)}
                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <Icon name="close" size={12} />
                  </button>
                </div>
              ))}
              {refs.length < 3 ? (
                <button
                  onClick={() => fileInput.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); void addFiles([...e.dataTransfer.files]); }}
                  // Empty: one full-width strip. With photos: a square tile beside
                  // them. (Both size classes at once let aspect-square win and
                  // turned the empty drop zone into a 290px square.)
                  className={`flex flex-col items-center justify-center gap-1 rounded-md border border-dashed text-[11px] transition-colors ${
                    dragOver ? "border-ed-accent bg-ed-active text-ed-text" : "border-ed-border text-ed-text-3 hover:border-ed-accent hover:text-ed-accent"
                  } ${refs.length === 0 ? "col-span-3 py-6" : "aspect-square"}`}
                  data-ai-drop
                >
                  <Icon name="add_photo_alternate" size={refs.length === 0 ? 22 : 16} />
                  {refs.length === 0 ? <span>Drop, paste or click to add a photo</span> : <span>Add</span>}
                </button>
              ) : null}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              hidden
              onChange={(e) => { void addFiles([...(e.target.files ?? [])]); e.target.value = ""; }}
            />
          </div>

          {/* What to match */}
          <div>
            <div className="mb-1.5 text-[12px] font-semibold text-ed-text">What should match?</div>
            <div className="flex flex-wrap gap-1.5">
              {LOOK_CATEGORIES.map((c) => {
                const on = cats.includes(c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => setCats((cs) => (on ? cs.filter((x) => x !== c.id) : [...cs, c.id]))}
                    aria-pressed={on}
                    className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                      on ? "border-ed-accent bg-ed-active text-ed-text" : "border-ed-border text-ed-text-3 hover:text-ed-text"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 500))}
            rows={2}
            placeholder="Anything specific? e.g. icier stone, softer shadow"
            className="w-full resize-none rounded-md border border-ed-border bg-ed-panel px-2 py-1.5 text-[12px] text-ed-text outline-none placeholder:text-ed-text-3 focus:border-ed-accent"
          />

          <button
            disabled={!canRun}
            onClick={() => void run()}
            className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-ed-ink text-[13px] font-semibold text-white transition-opacity hover:bg-black disabled:cursor-not-allowed disabled:opacity-40"
            data-ai-run
          >
            {working ? (
              <>
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                {STAGES[stage]}
              </>
            ) : (
              <>
                <Icon name="auto_awesome" size={15} />
                {result ? "Match again" : "Match this look"}
              </>
            )}
          </button>

          {error ? <Callout icon="error" tone="error">{error}</Callout> : null}

          {result && phase === "done" ? (
            <div className="rounded-lg border border-ed-border" data-ai-result>
              <div className="border-b border-ed-border px-2.5 py-2 text-[12px] leading-snug text-ed-text">
                {result.res.summary || "Done."}
              </div>
              {result.res.changes.length === 0 ? (
                <p className="px-2.5 py-2 text-[11px] text-ed-text-3">Already a close match — nothing needed changing.</p>
              ) : (
                <ul className="divide-y divide-ed-border/70">
                  {result.res.changes.map((c) => (
                    <li key={c.key} className="px-2.5 py-1.5">
                      <div className="flex items-baseline justify-between gap-2 text-[11px]">
                        <span className="truncate font-medium text-ed-text">{lookField(c.key)?.label ?? c.key}</span>
                        <span className="shrink-0 tabular-nums text-ed-text-2">
                          {fmt(result.before[c.key])} → <b className="text-ed-text">{fmt(c.value)}</b>
                        </span>
                      </div>
                      {c.reason ? <div className="text-[11px] leading-snug text-ed-text-3">{c.reason}</div> : null}
                    </li>
                  ))}
                </ul>
              )}
              {result.res.changes.length > 0 ? (
                <div className="flex items-center gap-1.5 border-t border-ed-border px-2.5 py-2">
                  <button
                    onClick={() => {
                      void engine.undo().then(() => { setResult(null); setPhase("idle"); }).catch((e: Error) => notice(e.message));
                    }}
                    className="flex h-6 items-center gap-1 rounded border border-ed-border px-2 text-[11px] text-ed-text-2 hover:border-ed-accent hover:text-ed-accent"
                  >
                    <Icon name="undo" size={13} /> Undo
                  </button>
                  <button
                    onClick={() => void run()}
                    disabled={!canRun}
                    title="Run again with a fresh render of the result, to close what is left"
                    className="flex h-6 items-center gap-1 rounded border border-ed-border px-2 text-[11px] text-ed-text-2 hover:border-ed-accent hover:text-ed-accent disabled:opacity-40"
                  >
                    <Icon name="refresh" size={13} /> Refine
                  </button>
                  <span className="flex-1" />
                  <button onClick={seeInAdvanced} className="text-[11px] font-semibold text-ed-accent hover:underline">
                    See in Advanced →
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
  );

  if (embedded) {
    return (
      <div data-ai-panel>
        <div className="space-y-3" onPaste={onPaste}>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div
      className="pointer-events-auto absolute top-0 bottom-0 z-10 overflow-hidden border-l border-ed-border bg-ed-panel shadow-[-2px_0_16px_rgba(0,0,0,0.04)]"
      style={{ right: RAIL_W, width: RIGHT_PANEL_W }}
      data-ai-panel
      onPaste={onPaste}
    >
      <div className="flex h-full flex-col">
        <div className="flex h-12 shrink-0 items-center gap-2 border-b border-ed-border px-4">
          <Icon name="auto_awesome" size={18} className="text-ed-accent" />
          <span className="truncate text-[15px] font-semibold text-ed-text">AI Look Match</span>
          <span className="flex-1" />
          <button
            title="Close"
            onClick={() => patchUi({ rightTab: null, lastRightTab: AI_PANEL })}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-ed-text-3 hover:bg-ed-hover"
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        {/* pr-1.5 + the bar's 6px stable gutter = the 12px on the left. */}
        <ScrollArea className="space-y-3 py-3 pl-3 pr-1.5">{content}</ScrollArea>
      </div>
    </div>
  );
}

function Callout({ icon, tone, children }: { icon: string; tone?: "error"; children: React.ReactNode }) {
  return (
    <div
      className={`flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[11px] leading-snug ${
        tone === "error" ? "bg-ed-red/10 text-ed-red" : "bg-ed-panel-2 text-ed-text-2"
      }`}
    >
      <Icon name={icon} size={14} className="mt-px shrink-0" />
      <span>{children}</span>
    </div>
  );
}
